import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import useNumbersSubscription from "../hooks/useNumbersSubscription";
import Engage                 from "../services/engage";

const ThreadsContext = createContext({});

export function ThreadsProvider({ initialFilter = "assigned", children }) {
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setActiveFilter] = useState(initialFilter);

  const setFilter = useCallback((activeFilter)=>{
    if(loading) return
    setActiveFilter(activeFilter)
  },[loading, setActiveFilter])

  const getThreads = useCallback(async () => {
    if (!loading) {
      setLoading(true);
      const res = await Engage.getConversations({ filter, page });
      if (res.error) {
        setError(res.message);
      } else {
        setThreads(page === 1 ? res.response : [...threads, ...res.response]);
      }
      setLoading(false);
    }
  }, [loading, setLoading, filter, page, setError, setThreads]);

  const refreshThreads = useCallback(async () => {
    const res = await Engage.getConversations({ filter, page: 1 });

    setPage(1);

    if (res.error) {
      setError(res.message);
    } else {
      setThreads(res.response);
    }
  }, [setError, setThreads, filter]);

  const updateThread = useCallback(
    (thread) => {
      let existingThread = threads.find((t) => t.id === thread.id);
      if (!existingThread) {
        return false;
      }

      setThreads(
        threads
          .map((t) => {
            if (thread.id == t.id) {
              return { ...t, ...thread };
            }
            return t;
          })
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      );

      return true;
    },
    [threads, setThreads]
  );

  const prevPage = useCallback(() => {
    if (page === 1 || loading) return;
    setPage(page - 1);
  }, [page, loading]);

  const nextPage = useCallback(() => {
    if (loading) return;
    setPage(page + 1)
  }, [page, loading]);

  useEffect(() => {
    getThreads();
  }, [filter, page]);

  const pusherConversationUpdateHandler = useCallback(
    (data) => {
      if (data?.id) {
        const result = updateThread(data);
        if (!result) {
          console.warn("Could not find thread to update. Refreshing threads.");
          setTimeout(() => refreshThreads(), 600);
        }
      }
    },
    [updateThread, refreshThreads]
  );

  useNumbersSubscription(
    "conversation-updated",
    pusherConversationUpdateHandler
  );

  return (
    <ThreadsContext.Provider
      value={{
        getThreads,
        refreshThreads,
        setFilter,
        prevPage,
        nextPage,
        threads,
        filter,
        page,
        loading,
        error,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
}

export default ThreadsContext;
